import type { Question } from '@/types';
import Condition from '@/utils/condition';
import TypeGuard from '@/utils/type-guard';

const pickLastPath = (arr: (string | string[])[]): string | string[] => {
  if (!arr || arr.length === 0) return '';
  const last = [...arr]
    .reverse()
    .find((item) => (typeof item === 'string' && item !== '') || (Array.isArray(item) && item.length > 0));
  return last ?? '';
};

/**
 * Function to get path based on question. Returns new path as array of question keys or URL
 * as string or null if Question does not have any path update or none of conditions were matched.
 */
export const getPath = (question: Question, answer: string): string | string[] | null => {
  // Case without choices key but with path key.
  if (!question.choices && question.path) {
    return Condition.getFirst<string[], null>(question.path, null);
  }
  // Case with choices key and multiple true. Case for cards with multiple select option.
  if (question.choices && question.multiple) {
    const path = question.choices
      .filter((choice) => answer.includes(choice.value))
      .map((choice) => choice.path)
      .filter((choice) => TypeGuard.isNotUndefined(choice));
    // Case where paths are strings, so only URLs, only one URL should be passed as new path
    if (path.every((p) => typeof p === 'string')) {
      // URL must be not empty string, last valid URL is selected, if there are no valid URLs null will be returned
      return path
        .filter((p) => p)
        .reverse()
        .reduce((_: string | null, p) => p, null);
    }
    // Case where paths are array of keys, so these paths must be merged in some order and under some rules.
    if (path.every((p) => Array.isArray(p))) {
      return path
        .filter((p) => p.length >= 1)
        .reduce((mergedPath, p) => Array.from(new Set([...mergedPath, ...p])), []);
    }
    // If in multiple case paths are of mixed type this case as considered as wrong configuration.
    return null;
  }
  // Case with choices key and multiple false. Case for cards with single select option.
  if (question.choices && !question.multiple) {
    const path = pickLastPath(
      question.choices
        .filter((choice) => choice.value === answer)
        .map((choice) => choice.path)
        .filter((choice) => TypeGuard.isNotUndefined(choice)),
    );
    // Path can be string URL (not empty string) or array of keys (not empty array). Other options are treated as false.
    return (typeof path === 'string' && path !== '') || (Array.isArray(path) && path.length >= 1) ? path : null;
  }
  // If question has no choices or path key, null is returned as sign that path should not be updated.
  return null;
};
